import React, { useMemo, useState } from "react";
import { gql } from "@apollo/client";
import { format, parseISO } from "date-fns";
import { useHrisIntegrationSidebarQuery } from "./HrisIntegrationDetailSidebar.generated";
import SidebarHeader, {
  SidebarContent,
  SidebarTitle,
} from "@src/features/sidebars/ui/SidebarHeader";
import InfoTable, { InfoTableItem } from "@src/lib/ui/InfoTable";
import PaginatedTable from "@src/lib/ui/PaginatedTable";
import {
  HrisEmployeeFragment,
  useHrisEmployeesLazyQuery,
  useHrisEmployeesQuery,
} from "./HrisEmployeeListContainer.generated";
import {
  HrisLocationFragment,
  useHrisLocationsLazyQuery,
  useHrisLocationsQuery,
} from "./HrisLocationListContainer.generated";
import {
  HrisTeamFragment,
  useHrisTeamsLazyQuery,
  useHrisTeamsQuery,
} from "./HrisRoleListContainer.generated";
import {
  HrisEmployeesInput,
  HrisIntegrationType,
  HrisLocationsInput,
  HrisTeamsInput,
  MergeBackfillRunsInput,
} from "@src/types.generated";
import {
  useMergeBackfillRunsLazyQuery,
  MergeBackfillRunFragment,
  useMergeBackfillRunsQuery,
} from "./HrisSyncsListContainer.generated";
import {
  QueryTabs,
  QueryTabRenderFns,
  QueryTabCopy,
  QueryTabCounts,
} from "@src/lib/ui/QueryTabs";
import { DetailSidebarType } from "@src/features/sidebars/detailSidebar";
import { listToReadableString } from "@src/lib/utils/strings";
import Text from "@src/lib/ui/Text";
import { useToast } from "@src/lib/hooks/useToast";
import useActionPermissions from "@src/features/app/hooks/useActionPermissions";
import { DeleteHrisIntegrationDialog } from "./DeleteHrisIntegrationDialog";
import { Button } from "@src/lib/ui/Button";
import { toast } from "sonner";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import { Ff } from "@src/types.generated";
import {
  useResyncHrisIntegrationMutation,
  useDeleteHrisIntegrationMutation,
} from "./HrisIntegrationsListContainer.generated";
import { GraphqlOperations } from "@src/types.generated";
import { useOpenMergeLink } from "./hooks/useOpenMergeLink";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@src/lib/ui/DropdownMenu";
import DotsIcon from "@src/lib/icons/18px/dots";
import GearOutlineIcon from "@src/lib/icons/18px/gear-outline";
import ArrowsRefreshIcon from "@src/lib/icons/18px/arrows-refresh";
import TrashOutlineIcon from "@src/lib/icons/18px/trash-outline";
import LinkIcon from "@src/lib/icons/18px/link";
import UserOutlineIcon from "@src/lib/icons/18px/user-outline";
import { getHrisIntegrationTypeDisplayName } from "./utils";
import IdBadge2OutlineIcon from "@src/lib/icons/18px/id-badge-2-outline";
import { MapPinIcon } from "lucide-react";
import LinkSlashOutlineIcon from "@src/lib/icons/18px/link-slash-outline";

type Props = {
  rowId: string;
  showHeader?: boolean;
};

const HrisIntegrationDetailSidebar: React.FC<Props> = ({
  rowId,
  showHeader = true,
}) => {
  const { addToast } = useToast();
  const [deleteIntegrationOpen, setDeleteIntegrationOpen] = useState(false);
  const { userActionPermissions } = useActionPermissions();
  const isResyncHrisIntegrationEnabled = useFeatureFlag(
    Ff.ResyncHrisIntegration,
  );
  const openMergeLink = useOpenMergeLink(rowId);
  const [resyncIntegration, { loading: isResyncing }] =
    useResyncHrisIntegrationMutation({
      onCompleted: (data) => {
        if (data.resyncHrisIntegration.success) {
          toast.success("HRIS integration resynced successfully!");
        } else {
          toast.error("Failed to resync HRIS integration");
        }
      },
      onError: () => {
        toast.error("An unexpected error occurred while resyncing");
      },
    });
  const [deleteHrisIntegration] = useDeleteHrisIntegrationMutation({
    refetchQueries: [GraphqlOperations.Query.HrisListHrisIntegrations],
    onCompleted: () => {
      addToast({ message: "HRIS integration deleted successfully" });
    },
  });

  const { data } = useHrisIntegrationSidebarQuery({
    variables: {
      id: rowId,
    },
    skip: !rowId,
  });
  const integration = data?.HrisIntegration;

  const employeesInputFilters: HrisEmployeesInput = useMemo(() => {
    return {
      filters: {
        hrisIntegrationId: { value: rowId },
        employmentStatus: { value: "ACTIVE" },
      },
    };
  }, [rowId]);

  const locationsInputFilters: HrisLocationsInput = useMemo(() => {
    return {
      filters: {
        hrisIntegrationId: { value: rowId },
      },
    };
  }, [rowId]);

  const teamsInputFilters: HrisTeamsInput = useMemo(() => {
    return {
      filters: {
        hrisIntegrationId: { value: rowId },
      },
    };
  }, [rowId]);

  const syncsInputFilters: MergeBackfillRunsInput = useMemo(() => {
    return {
      filters: {
        hrisIntegrationId: { value: rowId },
      },
    };
  }, [rowId]);

  // Fetch total counts for the tab badges
  const { data: employeesCountData } = useHrisEmployeesQuery({
    variables: {
      input: employeesInputFilters,
      pagination: { limit: 0 },
    },
    skip: !rowId,
  });

  const { data: locationsCountData } = useHrisLocationsQuery({
    variables: {
      input: locationsInputFilters,
      pagination: { limit: 0 },
    },
    skip: !rowId,
  });

  const { data: teamsCountData } = useHrisTeamsQuery({
    variables: {
      input: teamsInputFilters,
      pagination: { limit: 0 },
    },
    skip: !rowId,
  });

  const { data: syncsCountData } = useMergeBackfillRunsQuery({
    variables: {
      input: syncsInputFilters,
      pagination: { limit: 0 },
    },
    skip: !rowId,
  });

  const employeeColumns = [
    "name",
    "hrisId",
    "status",
    "location",
    "role",
    "syncedAt",
  ] as const;
  type EmployeeColumn = (typeof employeeColumns)[number];

  const locationColumns = ["hrisLocation", "opusLocation", "isLinked"] as const;
  type LocationColumn = (typeof locationColumns)[number];

  const teamColumns = ["hrisTeam", "opusRole", "isLinked"] as const;
  type TeamColumn = (typeof teamColumns)[number];

  const syncColumns = [
    "id",
    "queuedAt",
    "startedAt",
    "completedAt",
    "isNightlyResync",
  ] as const;
  type SyncColumn = (typeof syncColumns)[number];

  const tabCounts: QueryTabCounts<
    "details" | "employees" | "roles" | "locations" | "syncs"
  > = useMemo(() => {
    return {
      employees: employeesCountData?.HrisEmployees?.totalCount ?? undefined,
      roles: teamsCountData?.HrisTeams?.totalCount ?? undefined,
      locations: locationsCountData?.HrisLocations?.totalCount ?? undefined,
      syncs: syncsCountData?.MergeBackfillRuns?.totalCount ?? undefined,
    };
  }, [employeesCountData, teamsCountData, locationsCountData, syncsCountData]);

  const handleResync = () => {
    resyncIntegration({
      variables: {
        input: {
          hrisIntegrationId: rowId,
        },
      },
    });
  };

  const statusDisplay = integration?.status
    ? `${integration?.status.charAt(0).toUpperCase()}${integration?.status
        .toLowerCase()
        .slice(1)}`
    : "--";

  const tabKeys = [
    "details",
    "employees",
    "roles",
    "locations",
    "syncs",
  ] as const;
  type TabKey = (typeof tabKeys)[number];

  const tabLabels: QueryTabCopy<TabKey> = {
    details: "Details",
    employees: "Active HRIS employees",
    roles: "Roles",
    locations: "Locations",
    syncs: "Syncs",
  };

  const tabQueryTabRenderFns: QueryTabRenderFns<TabKey> = {
    details: () => <InfoTable items={items} />,
    employees: () => (
      <PaginatedTable<
        HrisEmployeeFragment,
        EmployeeColumn,
        typeof useHrisEmployeesQuery,
        typeof useHrisEmployeesLazyQuery,
        EmployeeColumn
      >
        defaultSort={{ column: "syncedAt", descending: true }}
        queryHook={useHrisEmployeesQuery}
        lazyQueryHook={useHrisEmployeesLazyQuery}
        getItems={(x) => x?.HrisEmployees?.objects ?? []}
        getTotalItemCount={(x) => x?.HrisEmployees?.totalCount ?? 0}
        itemId={(x) => x.id}
        input={employeesInputFilters}
        sidebarConfig={{
          tableId: "hris-integration-employees",
          sidebarType: DetailSidebarType.HrisEmployee,
          enabled: true,
        }}
        allColumns={[...employeeColumns]}
        columnSelection={{
          enabled: true,
          alwaysVisibleColumns: ["name"],
          defaultSelectedColumns: [...employeeColumns],
        }}
        columnTitles={{
          name: "Name",
          hrisId: "HRIS ID",
          location: "Location",
          role: "Role",
          status: "Status",
          syncedAt: "Synced from HRIS",
        }}
        renderFns={{
          name: (x) => (
            <div className="flex flex-1 items-center gap-2">
              <UserOutlineIcon className="text-gray-7" />
              <Text
                variant="compact-title"
                ellipsis
                ellipsisParentFlexDirection={"row"}
              >
                {`${x.firstName} ${x.lastName}`}
              </Text>
            </div>
          ),
          location: (x) =>
            listToReadableString(
              x.locationMemberships.map((memb) => memb.location.name),
            ),
          role: (x) =>
            listToReadableString(
              x.teamMemberships.map((memb) => memb.team.name),
            ),
          status: (x) => (x.user ? "Invited" : "Not invited"),
          syncedAt: (x) => format(parseISO(x.createdAt), "M/d/yyyy"),
          hrisId: (x) => x.remoteHrisId,
        }}
      />
    ),
    roles: () => (
      <PaginatedTable<
        HrisTeamFragment,
        TeamColumn,
        typeof useHrisTeamsQuery,
        typeof useHrisTeamsLazyQuery,
        TeamColumn
      >
        queryHook={useHrisTeamsQuery}
        lazyQueryHook={useHrisTeamsLazyQuery}
        getTotalItemCount={(x) => x?.HrisTeams.totalCount || 0}
        getItems={(x) => x?.HrisTeams.objects || []}
        itemId={(x) => x.id}
        input={teamsInputFilters}
        sidebarConfig={{
          tableId: "hris-integration-roles",
          sidebarType: DetailSidebarType.HrisRole,
          enabled: true,
        }}
        allColumns={["hrisTeam", "opusRole", "isLinked"]}
        columnSelection={{
          enabled: true,
          alwaysVisibleColumns: ["hrisTeam"],
          defaultSelectedColumns: ["hrisTeam", "opusRole", "isLinked"],
        }}
        columnTitles={{
          hrisTeam: "HRIS Role",
          opusRole: "Opus Role",
          isLinked: "Link",
        }}
        renderFns={{
          hrisTeam: (x) => (
            <div className="flex flex-1 items-center gap-2">
              <IdBadge2OutlineIcon className="text-gray-7" />
              <Text
                variant="compact-title"
                ellipsis
                ellipsisParentFlexDirection={"row"}
              >
                {x.name}
              </Text>
            </div>
          ),
          opusRole: (x) =>
            x.group ? (
              <div className="flex items-center">
                <LinkIcon />
                <Text variant="compact-description">{x.group.name}</Text>
              </div>
            ) : (
              <div className="flex items-center">
                <LinkSlashOutlineIcon className="text-gray-7" />
                <Text variant="compact-description" className="text-gray-7">
                  Not linked
                </Text>
              </div>
            ),
          isLinked: (x) => (x.group ? "Linked" : "Not linked"),
        }}
      />
    ),
    locations: () => (
      <PaginatedTable<
        HrisLocationFragment,
        LocationColumn,
        typeof useHrisLocationsQuery,
        typeof useHrisLocationsLazyQuery,
        LocationColumn
      >
        queryHook={useHrisLocationsQuery}
        lazyQueryHook={useHrisLocationsLazyQuery}
        getTotalItemCount={(x) => x?.HrisLocations.totalCount || 0}
        getItems={(x) => x?.HrisLocations.objects || []}
        itemId={(x) => x.id}
        input={locationsInputFilters}
        sidebarConfig={{
          tableId: "hris-integration-locations",
          sidebarType: DetailSidebarType.HrisLocation,
          enabled: true,
        }}
        allColumns={["hrisLocation", "opusLocation", "isLinked"]}
        columnSelection={{
          enabled: true,
          alwaysVisibleColumns: ["hrisLocation"],
          defaultSelectedColumns: ["hrisLocation", "opusLocation", "isLinked"],
        }}
        columnTitles={{
          hrisLocation: "HRIS Location",
          opusLocation: "Opus Location",
          isLinked: "Link",
        }}
        renderFns={{
          hrisLocation: (x) => (
            <div className="flex flex-1 items-center gap-2">
              <MapPinIcon className="size-[18px] text-gray-7" />
              <Text
                variant="compact-title"
                ellipsis
                ellipsisParentFlexDirection={"row"}
              >
                {x.name}
              </Text>
            </div>
          ),
          opusLocation: (x) =>
            x.group ? (
              <div className="flex items-center">
                <LinkIcon />
                <Text variant="compact-description">{x.group.name}</Text>
              </div>
            ) : (
              <div className="flex items-center">
                <LinkSlashOutlineIcon className="text-gray-7" />
                <Text variant="compact-description" className="text-gray-7">
                  Not linked
                </Text>
              </div>
            ),
          isLinked: (x) => (x.group ? "Linked" : "Not linked"),
        }}
      />
    ),
    syncs: () => (
      <PaginatedTable<
        MergeBackfillRunFragment,
        SyncColumn,
        typeof useMergeBackfillRunsQuery,
        typeof useMergeBackfillRunsLazyQuery,
        SyncColumn
      >
        key="integration-detail-sidebar-syncs-table"
        defaultSort={{ column: "queuedAt", descending: true }}
        queryHook={useMergeBackfillRunsQuery}
        lazyQueryHook={useMergeBackfillRunsLazyQuery}
        getItems={(x) => x?.MergeBackfillRuns?.objects ?? []}
        getTotalItemCount={(x) => x?.MergeBackfillRuns?.totalCount ?? 0}
        itemId={(x) => x.id}
        input={syncsInputFilters}
        sidebarConfig={{
          tableId: "hris-integration-syncs",
          sidebarType: DetailSidebarType.HrisSync,
          enabled: true,
        }}
        allColumns={[...syncColumns]}
        columnSelection={{
          enabled: true,
          alwaysVisibleColumns: ["id"],
          defaultSelectedColumns: [...syncColumns],
        }}
        columnTitles={{
          id: "Sync ID",
          queuedAt: "Queued at",
          startedAt: "Started at",
          completedAt: "Completed at",
          isNightlyResync: "Is nightly resync",
        }}
        renderFns={{
          id: (x) => (
            <div className="flex flex-1 items-center gap-2">
              <ArrowsRefreshIcon className="text-gray-7" />
              <Text
                variant="compact-title"
                ellipsis
                ellipsisParentFlexDirection={"row"}
              >
                {x.id}
              </Text>
            </div>
          ),
          queuedAt: (x) =>
            x.queuedAt ? format(parseISO(x.queuedAt), "M/d/yyyy h:mm a") : "-",
          startedAt: (x) =>
            x.startedAt
              ? format(parseISO(x.startedAt), "M/d/yyyy h:mm a")
              : "-",
          completedAt: (x) =>
            x.completedAt
              ? format(parseISO(x.completedAt), "M/d/yyyy h:mm a")
              : "-",
          isNightlyResync: (x) => (x.isNightlyResync ? "Yes" : "No"),
        }}
      />
    ),
  };

  const items: InfoTableItem[] = useMemo(() => {
    const baseItems: InfoTableItem[] = [
      {
        label: "Type",
        value: getHrisIntegrationTypeDisplayName(integration?.type),
      },
    ];

    // Only show provider, status, and status detail for Merge integrations
    if (integration?.type === HrisIntegrationType.Merge) {
      baseItems.push(
        {
          label: "HRIS provider",
          value: integration?.hrisProvider || "--",
        },
        {
          label: "Status",
          value: statusDisplay,
        },
        {
          label: "Status detail",
          value: integration?.statusDetail || "--",
        } as InfoTableItem,
      );
    }

    baseItems.push(
      {
        label: "Linked at",
        value: integration?.createdAt
          ? format(parseISO(integration.createdAt), "M/d/yyyy h:mm a")
          : "--",
      },
      {
        label: "Initial sync completed at",
        value: integration?.initialSyncCompletedAt
          ? format(
              parseISO(integration.initialSyncCompletedAt),
              "M/d/yyyy h:mm a",
            )
          : "--",
      } as InfoTableItem,
      {
        label: "Last received data at",
        value: integration?.lastReceivedDataAt
          ? format(parseISO(integration.lastReceivedDataAt), "M/d/yyyy h:mm a")
          : "--",
      } as InfoTableItem,
    );

    return baseItems;
  }, [integration, statusDisplay]);
  if (!integration) return null;
  return (
    <div className="flex h-full flex-col">
      {showHeader && <SidebarHeader />}
      <SidebarTitle
        title={integration.id}
        icon={<LinkIcon className="size-[18px] text-gray-9" />}
        sidebarType={DetailSidebarType.HrisIntegration}
      />
      <div className="px-4">
        <div className="flex gap-1">
          {integration.type === HrisIntegrationType.Merge && (
            <Button
              variant="outline"
              onClick={openMergeLink}
              disabled={!userActionPermissions?.manageOrgIntegrations}
            >
              <GearOutlineIcon className="text-muted-foreground" />
              Modify
            </Button>
          )}
          {isResyncHrisIntegrationEnabled && (
            <Button
              variant="outline"
              onClick={handleResync}
              disabled={isResyncing}
            >
              <ArrowsRefreshIcon className="text-muted-foreground" />
              Resync
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon">
                <DotsIcon />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="bottom">
              <DropdownMenuItem
                className="cursor-pointer"
                disabled={!userActionPermissions?.manageOrgIntegrations}
                onClick={() => {
                  setDeleteIntegrationOpen(true);
                }}
              >
                <TrashOutlineIcon className="text-gray-10" />
                <Text variant="compact-title">Delete</Text>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <SidebarContent>
        <div className="flex min-h-0 flex-1 flex-col">
          <QueryTabs<TabKey>
            tabs={[...tabKeys]}
            labels={tabLabels}
            render={tabQueryTabRenderFns}
            counts={tabCounts as QueryTabCounts<TabKey>}
            defaultTab="details"
            queryParamKey="hris-integration-tab"
          />
        </div>
      </SidebarContent>
      {deleteIntegrationOpen && (
        <DeleteHrisIntegrationDialog
          open={deleteIntegrationOpen}
          onOpenChange={setDeleteIntegrationOpen}
          onDelete={async () => {
            await deleteHrisIntegration({
              variables: {
                input: {
                  hrisIntegrationId: integration.id,
                },
              },
            });
          }}
        />
      )}
    </div>
  );
};

// GraphQL query for the sidebar - reuses the same pattern as the main list
gql`
  query HrisIntegrationSidebar($id: UUID!) {
    HrisIntegration(id: $id) {
      id
      type
      createdAt
      hrisProvider
      initialSyncCompletedAt
      status
      statusDetail
      lastReceivedDataAt
    }
  }
`;

export default HrisIntegrationDetailSidebar;
